import { convertUnit, siPrefixes, unitDefinitions } from './unitConversion';

export interface ConversionStep {
  expression: string;
  result: number;
  unit: string;
}

export interface FactorLabelConversion {
  input: number;
  fromUnit: string;
  toUnit: string;
  conversionFactor: number;
  result: number;
  steps: ConversionStep[];
}

export function factorLabelConversion(value: number, fromUnit: string, toUnit: string): FactorLabelConversion {
  const result = convertUnit(value, fromUnit, toUnit);
  const temperatureUnits = ['degC', 'K', 'degF'];
  if (temperatureUnits.includes(fromUnit) && temperatureUnits.includes(toUnit)) {
    const expressions: Record<string, string> = {
      'degC->K': `${value} + 273.15`,
      'K->degC': `${value} - 273.15`,
      'degF->K': `(${value} - 32) x 5/9 + 273.15`,
      'K->degF': `(${value} - 273.15) x 9/5 + 32`,
      'degC->degF': `${value} x 9/5 + 32`,
      'degF->degC': `(${value} - 32) x 5/9`,
    };
    return {
      input: value,
      fromUnit,
      toUnit,
      conversionFactor: 1,
      result,
      steps: [{ expression: expressions[`${fromUnit}->${toUnit}`] || `${value}`, result, unit: toUnit }],
    };
  }
  const from = unitDefinitions[fromUnit];
  const to = unitDefinitions[toUnit];
  const conversionFactor = from && to ? from.factor / to.factor : result / value;
  return {
    input: value,
    fromUnit,
    toUnit,
    conversionFactor,
    result,
    steps: [{ expression: `${value} ${fromUnit} x (${conversionFactor} ${toUnit} / ${fromUnit})`, result, unit: toUnit }],
  };
}

export function convertUsingPrefixes(value: number, fromPrefix: string, toPrefix: string, unit = 'unit'): FactorLabelConversion {
  const fromFactor = siPrefixes[fromPrefix];
  const toFactor = siPrefixes[toPrefix];
  if (fromFactor === undefined || toFactor === undefined) throw new RangeError('Unknown SI prefix.');
  const conversionFactor = fromFactor / toFactor;
  const result = Number((value * conversionFactor).toPrecision(15));
  const fromSymbol = `${fromPrefix}${unit}`;
  const toSymbol = `${toPrefix}${unit}`;
  return {
    input: value,
    fromUnit: fromSymbol,
    toUnit: toSymbol,
    conversionFactor,
    result,
    steps: [{ expression: `${value} ${fromSymbol} x (${conversionFactor} ${toSymbol} / ${fromSymbol})`, result, unit: toSymbol }],
  };
}

export function toScientificNotation(value: number, significantFigures = 3): string {
  if (!Number.isFinite(value)) throw new TypeError('Value must be finite.');
  if (!Number.isInteger(significantFigures) || significantFigures < 1) throw new RangeError('Significant figures must be a positive integer.');
  if (value === 0) return `0 x 10^0`;
  const exponent = Math.floor(Math.log10(Math.abs(value)));
  const coefficient = value / (10 ** exponent);
  return `${Number(coefficient.toPrecision(significantFigures))} x 10^${exponent}`;
}